import { useEffect, useRef, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { ShoppingBag, X, Upload } from "lucide-react";

export default function ContactsTab() {
  const [items, setItems] = useState([]);
  const [limit, setLimit] = useState(5000);
  const [form, setForm] = useState({ name: "", email: "", tags: "" });
  const [productKeys, setProductKeys] = useState([]);
  const [tagging, setTagging] = useState(null); // contact id currently being tagged
  const [tagChoice, setTagChoice] = useState("");

  const load = async () => {
    const r = await api.get("/contacts");
    setItems(r.data.contacts);
    setLimit(r.data.limit);
  };
  const loadKeys = async () => {
    try {
      const r = await api.get("/automation/segments");
      setProductKeys(r.data.product_keys || []);
    } catch { /* non-fatal */ }
  };
  useEffect(() => { load(); loadKeys(); }, []);

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/contacts", { name: form.name, email: form.email, tags: form.tags.split(",").map(t=>t.trim()).filter(Boolean) });
      toast.success("Contact added"); setForm({ name: "", email: "", tags: "" }); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not add"); }
  };

  const tagBuyer = async (cid) => {
    if (!tagChoice) return;
    try {
      await api.post(`/contacts/${cid}/tag-purchased`, { product_key: tagChoice });
      toast.success("Contact tagged as buyer");
      setTagging(null); setTagChoice("");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not tag"); }
  };

  const untagBuyer = async (cid, key) => {
    try {
      await api.post(`/contacts/${cid}/untag-purchased`, { product_key: key });
      toast.success("Tag removed");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not untag"); }
  };

  const keyToLabel = (k) => (productKeys.find(p => p.key === k)?.label || k).replace(/^.*·\s*/, "");

  /* ────── CSV upload (any spreadsheet with name, email, phone columns) ────── */
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const handleCsvUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const r = await api.post("/contacts/import-csv", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const d = r.data || {};
      toast.success(`★ Added ${d.added}. Skipped ${d.duplicates} duplicates, ${d.skipped} bad rows.`);
      if (d.hit_limit) toast.error(`Hit contact limit — ${d.hit_limit} rows didn't fit. Upgrade for more.`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="space-y-6" data-testid="contacts-tab">
      <div className="flex justify-between items-end flex-wrap gap-3">
        <div>
          <div className="text-xs tracking-wider-cap text-gold-deep">Contacts</div>
          <h2 className="font-serif-display text-3xl text-red-wine">Your people</h2>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="text-sm text-foreground">{items.length.toLocaleString()} / {limit.toLocaleString()} contacts</div>
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            data-testid="contacts-csv-upload-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-xs font-bold uppercase tracking-wider border-2 hover:bg-white transition disabled:opacity-50"
            style={{ borderColor: "#0F172A", color: "#0F172A" }}
          >
            <Upload className="w-4 h-4" />
            {uploading ? "Uploading…" : "Import CSV"}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept=".csv,text/csv"
            onChange={handleCsvUpload}
            className="hidden"
            data-testid="contacts-csv-file"
          />
        </div>
      </div>

      <div className="rounded-md bg-white/60 border border-border p-3 text-xs leading-relaxed" style={{ color: "#3D3D3D" }}>
        <strong className="text-red-wine">CSV tip:</strong> Got a list of your own contacts in a spreadsheet? Save it as a CSV and drop it here. Works with any header — Name, Phone, Website, Address, Email, Tags. Duplicates are auto-skipped.
      </div>
      <form onSubmit={add} className="faith-card p-6 grid md:grid-cols-4 gap-3 items-end" data-testid="contact-form">
        <input required placeholder="Name" className="px-4 py-3 rounded-md border border-border bg-white" value={form.name} onChange={e=>setForm({...form, name:e.target.value})} data-testid="contact-name"/>
        <input required type="email" placeholder="Email" className="px-4 py-3 rounded-md border border-border bg-white" value={form.email} onChange={e=>setForm({...form, email:e.target.value})} data-testid="contact-email"/>
        <input placeholder="Tags (comma separated)" className="px-4 py-3 rounded-md border border-border bg-white" value={form.tags} onChange={e=>setForm({...form, tags:e.target.value})} data-testid="contact-tags"/>
        <button className="btn-primary" data-testid="contact-add">Add</button>
      </form>
      <div className="faith-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white">
            <tr className="text-left">
              <th className="p-3">Name</th>
              <th className="p-3">Email</th>
              <th className="p-3">Phone</th>
              <th className="p-3">Tags</th>
              <th className="p-3">Bought</th>
              <th className="p-3">Added</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.map(c => {
              const purchased = c.purchased || [];
              return (
                <tr key={c.id} className="border-t border-border/60 align-top" data-testid={`contact-row-${c.id}`}>
                  <td className="p-3 font-medium">
                    {c.name}
                    {c.website && <div className="text-[11px] text-foreground/55"><a href={c.website.startsWith("http") ? c.website : `https://${c.website}`} target="_blank" rel="noopener noreferrer" className="hover:underline">{c.website}</a></div>}
                  </td>
                  <td className="p-3">{c.email || <span className="text-foreground/40 italic text-[12px]">—</span>}</td>
                  <td className="p-3">{c.phone || <span className="text-foreground/40 italic text-[12px]">—</span>}</td>
                  <td className="p-3 text-foreground">{(c.tags||[]).join(", ")}</td>
                  <td className="p-3" data-testid={`contact-purchased-${c.id}`}>
                    {purchased.length === 0 ? (
                      <span className="text-foreground/50 italic text-[12px]">no purchases</span>
                    ) : (
                      <div className="flex flex-wrap gap-1.5">
                        {purchased.map(k => (
                          <span key={k} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-wine/10 text-red-wine text-[11px] font-bold border border-red-wine/30">
                            {keyToLabel(k)}
                            <button onClick={() => untagBuyer(c.id, k)}
                                    className="opacity-60 hover:opacity-100"
                                    data-testid={`untag-${c.id}-${k}`}
                                    title={`Remove ${k} tag`}>
                              <X className="w-3 h-3"/>
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="p-3 text-foreground">{new Date(c.created_at).toLocaleDateString()}</td>
                  <td className="p-3">
                    {tagging === c.id ? (
                      <div className="flex items-center gap-1">
                        <select value={tagChoice} onChange={e => setTagChoice(e.target.value)}
                                className="px-2 py-1 rounded border border-border bg-white text-[11px]"
                                data-testid={`tag-select-${c.id}`}>
                          <option value="">Pick product…</option>
                          {productKeys.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
                        </select>
                        <button onClick={() => tagBuyer(c.id)}
                                disabled={!tagChoice}
                                className="px-2 py-1 rounded bg-red-wine text-white text-[11px] font-bold disabled:opacity-40"
                                data-testid={`tag-save-${c.id}`}>
                          Save
                        </button>
                        <button onClick={() => { setTagging(null); setTagChoice(""); }}
                                className="px-2 py-1 rounded border border-border text-[11px]">
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button onClick={() => { setTagging(c.id); setTagChoice(""); }}
                              className="inline-flex items-center gap-1 text-[11px] text-red-wine hover:underline"
                              data-testid={`tag-buyer-${c.id}`}>
                        <ShoppingBag className="w-3.5 h-3.5"/> Mark as buyer
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {items.length === 0 && <tr><td colSpan="6" className="p-6 text-center text-foreground">No contacts yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
